import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { buildCountModeRateLabel, canStackPremiums } from '../utils/countModeHourlyPremiumUtils';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { NumberInput } from '@/components/ui/number-input';
import { Switch } from '@/components/ui/switch';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { SurchargeMethodPanel } from '@/components/config_forms/shared/surcharge/SurchargeMethodPanel';
import { useMethodSwapAnimation } from '@/components/config_forms/shared/surcharge/useMethodSwapAnimation';
import {
  TIMELINE_AXIS_LABEL_CLASS,
  TIMELINE_NOTE_TEXT_CLASS,
} from '@/components/config_forms/shared/help-graphics/timeline/timelineTextTokens';
import { OTScaledNote } from '@/components/config_forms/shared/help-graphics/OTScaledNote';
import { ControlRow } from '@/components/config_forms/shared/ControlRow';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface CountModeHourlyPremiumGraphicProps {
  premiumType: 'fixed' | 'percentage';
  premiumValue: number;
  premiumBasis: 'base_rate' | 'ot_inclusive';
  premiumStacking: 'flat' | 'stacked';
  requiredBreaks: number;
  requiredBreaksHalfDay: number | null;
  thresholdHours: number | null;
  thresholdFromBilling: number | null;
  additionalBreakIntervalHours: number | null;
  onPremiumTypeChange?: (type: 'fixed' | 'percentage') => void;
  onPremiumBasisChange?: (basis: 'base_rate' | 'ot_inclusive') => void;
  onPremiumValueChange?: (value: number | null) => void;
  onPremiumStackingChange?: (stacking: 'flat' | 'stacked') => void;
}

/* ------------------------------------------------------------------ */
/*  SurchargeNote                                                      */
/* ------------------------------------------------------------------ */

type NoteVariant = 'no-stack' | 'flat' | 'stacked';

interface SurchargeNoteProps {
  variant: NoteVariant;
  rateLabel: string;
  otInclusive: boolean;
}

const SurchargeNote: React.FC<SurchargeNoteProps> = ({ variant, rateLabel, otInclusive }) => {
  const isStrong = variant === 'stacked';

  const borderColor = isStrong
    ? 'color-mix(in srgb, var(--primary-text), transparent 82%)'
    : 'color-mix(in srgb, var(--primary-text), transparent 88%)';
  const backgroundColor = isStrong
    ? 'color-mix(in srgb, var(--primary-text), transparent 94%)'
    : 'color-mix(in srgb, var(--primary-text), transparent 96%)';

  let content: React.ReactNode;
  if (variant === 'no-stack') {
    content = (
      <>
        <strong className="font-medium" style={{ color: 'color-mix(in srgb, var(--primary-text), transparent 10%)' }}>
          Surcharge:
        </strong>{' '}
        If the required break is missed, a premium of {rateLabel} is applied across total shift hours.
      </>
    );
  } else if (variant === 'flat') {
    content = (
      <>
        <strong className="font-medium" style={{ color: 'color-mix(in srgb, var(--primary-text), transparent 10%)' }}>
          Single Premium:
        </strong>{' '}
        Premium applied once regardless of how many breaks are missed.
      </>
    );
  } else {
    content = (
      <>
        <strong className="font-medium" style={{ color: 'color-mix(in srgb, var(--primary-text), transparent 10%)' }}>
          Stacked:
        </strong>{' '}
        Premium applied per missed break — each adds a full premium across total shift hours.
        {otInclusive && ' Overtime hours contribute more.'}
      </>
    );
  }

  return (
    <div
      className={`mt-1 mb-0 px-2 py-2 rounded-[0.25rem] border motion-safe:transition-[border-color,background-color] motion-safe:duration-200 ${TIMELINE_NOTE_TEXT_CLASS}`}
      style={{ borderColor, backgroundColor }}
    >
      {content}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  CountModeHourlyPremiumGraphic (exported)                           */
/* ------------------------------------------------------------------ */

export const CountModeHourlyPremiumGraphic: React.FC<CountModeHourlyPremiumGraphicProps> = ({
  premiumType,
  premiumValue,
  premiumBasis,
  premiumStacking,
  requiredBreaks,
  requiredBreaksHalfDay,
  thresholdHours,
  thresholdFromBilling,
  additionalBreakIntervalHours,
  onPremiumTypeChange,
  onPremiumBasisChange,
  onPremiumValueChange,
  onPremiumStackingChange,
}) => {
  const stackingPossible = canStackPremiums({
    requiredBreaks,
    requiredBreaksHalfDay,
    thresholdHours,
    thresholdFromBilling,
    additionalBreakIntervalHours,
  });

  const isStacked = stackingPossible && premiumStacking === 'stacked';
  const rateLabel = buildCountModeRateLabel(premiumType, premiumValue, premiumBasis, isStacked);

  let noteVariant: NoteVariant;
  if (!stackingPossible) {
    noteVariant = 'no-stack';
  } else if (isStacked) {
    noteVariant = 'stacked';
  } else {
    noteVariant = 'flat';
  }

  const hasCallbacks = !!(onPremiumTypeChange || onPremiumBasisChange || onPremiumValueChange || onPremiumStackingChange);
  const notePanelKey = `${noteVariant}|${premiumBasis}`;
  const {
    containerStyle: noteContainerStyle,
    containerRef: noteContainerRef,
    activePanelRef: notePanelRef,
    fadeDuration: noteFadeDuration,
  } = useMethodSwapAnimation(notePanelKey);

  return (
    <div data-testid="count-mode-hourly-premium-graphic">
      {/* Shift bar — full width, amber tint */}
        <div
          className="flex items-center justify-center h-14 rounded-[0.25rem] overflow-hidden bg-primary/[0.06]"
          style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--border), transparent 40%)' }}
        >
          <span className="font-mono text-[9px] sm:text-[11px] md:text-[12px] font-semibold text-primary-text/95 truncate whitespace-nowrap overflow-hidden max-w-[82px] px-1">
            {rateLabel}
          </span>
        </div>

        {/* Axis labels */}
        <div className="flex justify-between items-start mt-1">
          <span className={TIMELINE_AXIS_LABEL_CLASS}>
            Start
          </span>
          {stackingPossible && (
            <span className={`${TIMELINE_AXIS_LABEL_CLASS} text-destructive-text/80`}>
              Missed Breaks: 2
            </span>
          )}
          <span className={TIMELINE_AXIS_LABEL_CLASS}>
            End
          </span>
        </div>

        <OTScaledNote visible={premiumBasis === 'ot_inclusive'} />

        {/* Surcharge note */}
      <div
        ref={noteContainerRef}
        className="overflow-hidden"
        style={noteContainerStyle}
        data-anim-audit="count-mode-premium-note-container"
      >
        <div className="relative grid">
          <AnimatePresence mode="sync" initial={false}>
            <SurchargeMethodPanel
              key={notePanelKey}
              activePanelRef={notePanelRef}
              transitionDuration={noteFadeDuration}
            >
              <div data-anim-audit="count-mode-premium-note-panel" data-anim-audit-state={notePanelKey}>
                <SurchargeNote
                  variant={noteVariant}
                  rateLabel={rateLabel}
                  otInclusive={premiumBasis === 'ot_inclusive'}
                />
              </div>
            </SurchargeMethodPanel>
          </AnimatePresence>
        </div>
      </div>

      {/* Controls — combined card */}
      {hasCallbacks && (
        <div className="mt-3 rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20">
          {/* Premium Type */}
          {onPremiumTypeChange && (
            <ControlRow layout="responsive" label="Premium Type" description="Choose a flat dollar amount per hour or a percentage of your hourly rate." data-anim-audit="count-mode-premium-type-row">
              <SegmentedControl
                ariaLabel="Premium Type"
                options={[
                  { value: 'fixed', label: 'Dollar' },
                  { value: 'percentage', label: 'Percentage' },
                ]}
                value={premiumType}
                onChange={(v) => onPremiumTypeChange(v as 'fixed' | 'percentage')}
                className="w-full sm:w-auto sm:min-w-[220px] self-start sm:self-auto"
                size="sm"
              />
            </ControlRow>
          )}

          {/* Hourly Premium */}
          {onPremiumValueChange && (
            <ControlRow
              layout="responsive"
              label="Hourly Premium"
              description={premiumType === 'fixed'
                ? 'Dollar amount added per hour worked.'
                : 'Percentage of your hourly rate added per hour worked.'}
              data-anim-audit="count-mode-premium-value-row"
            >
              <div className="self-start sm:self-auto">
                <NumberInput
                  aria-label="Hourly premium"
                  size="xs"
                  touchTarget
                  value={premiumValue}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    onPremiumValueChange(isNaN(val) ? null : val);
                  }}
                  unit={premiumType === 'fixed' ? '$' : '%'}
                  unitPosition={premiumType === 'fixed' ? 'left' : 'right'}
                  step={premiumType === 'fixed' ? 1 : 5}
                  minValue={0}
                />
              </div>
            </ControlRow>
          )}

          {/* Scale with Overtime */}
          {onPremiumBasisChange && (
            <ControlRow layout="responsive" label="Scale with Overtime" description="Scale the premium by the active overtime multiplier." data-anim-audit="count-mode-premium-basis-row">
              <Switch
                aria-label="Scale with overtime"
                data-anim-audit="count-mode-premium-basis-switch"
                className="self-start sm:self-auto"
                checked={premiumBasis === 'ot_inclusive'}
                onCheckedChange={(checked) => {
                  const nextBasis = checked ? 'ot_inclusive' : 'base_rate';
                  if (nextBasis !== premiumBasis) onPremiumBasisChange(nextBasis);
                }}
              />
            </ControlRow>
          )}

          {/* Stack Premiums — only visible when stacking is possible */}
          <AnimatedCollapse open={stackingPossible && !!onPremiumStackingChange}>
            {onPremiumStackingChange && (
              <ControlRow layout="responsive" label="Stack Premiums" description="Multiply the premium by the number of missed breaks." data-anim-audit="count-mode-premium-stacking-row">
                <Switch
                  aria-label="Stack premiums"
                  data-anim-audit="count-mode-premium-stacking-switch"
                  className="self-start sm:self-auto"
                  checked={isStacked}
                  onCheckedChange={(checked) => {
                    onPremiumStackingChange(checked ? 'stacked' : 'flat');
                  }}
                />
              </ControlRow>
            )}
          </AnimatedCollapse>
        </div>
      )}
    </div>
  );
};
